import { jsonToFormData } from 'util/jsonToFormData';
import { selectApiData, selectApiNoContent } from 'util/selector/apiSelector';
import { selectResponseData } from 'util/selector/axiosSelector';
import { createPaginationQuery } from 'util/createPaginationQuery';

import { ApiMessage } from 'api/const';
import { WithData } from 'api/interfaces';
import { AxiosInstance } from 'axios';
import { Dayjs } from 'dayjs';
import { ProjectSortBy } from 'model/ProjectSortBy';
import { ProjectStatus } from 'model/ProjectStatus';
import { SortDirection } from 'shared/constants';
import { PaginationModel } from 'model/Pagination.dto';
import { ProjectKind } from 'model/ProjectKind';
import { companyApi } from 'container/companyApi';

import { CompanyModel, mapCompanyModelToDto } from './dto/Company.dto';
import { mapProjectDtoToModel, ProjectDto, ProjectModel, ProjectPostDto } from './dto/Project.dto';

export type ProjectQuery = PaginationQuery & {
  searchText: string;
  sortBy: ProjectSortBy;
  status: ProjectStatus | null;
};

export type PaginationQuery = {
  page: number;
};

export type ProjectListParams = {
  page: number;
  limit?: number;
  searchText: string;
  sortBy: ProjectSortBy;
  status?: ProjectStatus;
  circleId?: string;
};

export type ProjectUpdateParams = {
  company: CompanyModel;
  projectId: number;
  logo?: File;
  ssoEnabled?: boolean;
  multiLanguageEnabled?: boolean;
  customKioskCodesEnabled?: boolean;
  aiSummariesEnabled?: boolean;
};

export type ProjectUpdateSpecificParams = {
  company: CompanyModel;
  projectId: number;
  fieldName: string;
  value: boolean;
  logo?: File;
};

export type ProjectCreateModel = { company: CompanyModel; kind: ProjectKind };

export class ProjectApi {
  constructor(private client: AxiosInstance) {}

  list(params: ProjectListParams): Promise<{ list: ProjectModel[]; pagination: PaginationModel }> {
    const { page, limit, searchText, sortBy, status, circleId } = params;
    const sort = sortBy === ProjectSortBy.LastModified ? 'updatedAt' : 'liveStart';

    const query = createPaginationQuery({
      page,
      search: searchText,
      filters: {
        status,
        'company.circleId': circleId,
      },
      limit,
      sort: {
        [sort]: SortDirection.DESC,
      },
    });

    return this.client
      .get(`/api/v1/project${query}`)
      .then((response) => selectResponseData(response) || [])
      .then((result) => {
        return {
          list: result._embedded.items.map(mapProjectDtoToModel),
          pagination: result,
        };
      });
  }

  get(projectId: number): Promise<ProjectModel> {
    return this.client
      .get<WithData<ProjectDto>>(`/api/v1/project/${projectId}`)
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  async create(createModel: ProjectCreateModel, options: { logo?: File }): Promise<ProjectModel> {
    const { company, kind } = createModel;

    const companyId = company.id
      ? company.id
      : await companyApi
          .create(mapCompanyModelToDto(company), options.logo)
          .then((entry) => entry.id);

    const data: ProjectPostDto = {
      kind,
      companyId: Number(companyId),
    };

    return this.client
      .post<WithData<ProjectDto>>('/api/v1/project', data)
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  update(projectUpdateParams: ProjectUpdateParams): Promise<ProjectModel> {
    const {
      company,
      projectId,
      logo,
      ssoEnabled,
      multiLanguageEnabled,
      customKioskCodesEnabled,
      aiSummariesEnabled,
    } = projectUpdateParams;

    const companyDto = mapCompanyModelToDto(company);

    const data = jsonToFormData({
      company: {
        name: companyDto.name,
        brandColor: companyDto.brandColor,
        timezone: companyDto.timezone,
        approxEmployeeNumber: companyDto.approxEmployeeNumber,
        sectors: companyDto.sectors,
        logoFile: logo,
        settings: {
          ...(ssoEnabled === undefined ? {} : { ssoEnabled: ssoEnabled ? 1 : 0 }),
          ...(multiLanguageEnabled === undefined
            ? {}
            : { multiLanguageEnabled: multiLanguageEnabled ? 1 : 0 }),
          ...(customKioskCodesEnabled === undefined
            ? {}
            : { customKioskCodesEnabled: customKioskCodesEnabled ? 1 : 0 }),
          ...(aiSummariesEnabled === undefined
            ? {}
            : { aiSummariesEnabled: aiSummariesEnabled ? 1 : 0 }),
        },
      },
    });

    return this.client
      .post(`/api/v1/project/${projectId}/company`, data)
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  updateSpecific(projectUpdateSpecificParams: ProjectUpdateSpecificParams): Promise<ProjectModel> {
    const { company, projectId, fieldName, value, logo } = projectUpdateSpecificParams;

    const companyDto = mapCompanyModelToDto(company);

    const data = jsonToFormData({
      company: {
        name: companyDto.name,
        brandColor: companyDto.brandColor,
        timezone: companyDto.timezone,
        approxEmployeeNumber: companyDto.approxEmployeeNumber,
        sectors: companyDto.sectors,
        logoFile: logo,
        settings: {
          [fieldName]: value,
        },
      },
    });

    return this.client
      .post(`/api/v1/project/${projectId}/company`, data)
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  close(projectId: number): Promise<ProjectModel> {
    return this.client
      .post(`api/v1/project/${projectId}/close`)
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  delete(projectId: number): Promise<void> {
    return this.client
      .delete<ApiMessage.Deleted>(`/api/v1/project/${projectId}`)
      .then(selectApiNoContent);
  }

  reopen(liveStop: Dayjs, projectId: number): Promise<ProjectModel> {
    const adjustedLiveStop = liveStop.utcOffset(0, true);
    const data = jsonToFormData({
      liveStop: adjustedLiveStop.toISOString(),
    });

    return this.client
      .post(`/api/v1/project/${projectId}/re-open`, data)
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  createFirstPage(projectId: number): Promise<ProjectModel> {
    return this.client
      .post<WithData<ProjectDto>>(`/api/v1/project/${projectId}/survey`)
      .then(selectApiData)
      .then(mapProjectDtoToModel);
  }

  publish(projectId: number, generateNewAiInsights: boolean): Promise<void> {
    return this.client.post(`/api/v1/project/${projectId}/publish`, { generateNewAiInsights });
  }

  rename(projectId: number, name: string): Promise<void> {
    return this.client.put(`/api/v1/project/${projectId}/name`, { name });
  }

  activateAllUsersDashboardAccess(projectId: number): Promise<void> {
    return this.client.post(`api/v1/project/${projectId}/activate-dashboard-access`);
  }

  deactivateAllUsersDashboardAccess(projectId: number): Promise<void> {
    return this.client.post(`api/v1/project/${projectId}/deactivate-dashboard-access`);
  }

  deactivateUserDashboardAccess(projectId: number, userId: string): Promise<void> {
    return this.client
      .post(`/api/v1/project/${projectId}/user/${userId}/deactivate-dashboard-access`)
      .then(selectApiNoContent);
  }

  activateUserDashboardAccess(projectId: number, userId: string): Promise<void> {
    return this.client
      .post(`/api/v1/project/${projectId}/user/${userId}/activate-dashboard-access`)
      .then(selectApiNoContent);
  }
}
